import Response from '../modules/classes/Response';

class WikiController {
    constructor(apiService) {
        this.apiService = apiService;
    }

    /**
     * Get all wiki articles with pagination and filtering
     * @param {Object} options - Query options
     * @param {number} [options.page=1] - Page number
     * @param {number} [options.pageSize=20] - Items per page
     * @param {string} [options.sort='latest'] - Sort order (latest, popular)
     * @param {string} [options.category] - Filter by category
     * @param {string} [options.search] - Search query
     * @returns {Promise<Response>} - Response containing paginated articles
     */
    async getWikiArticles(options = {}) {
        try {
            const params = {
                page: options.page || 1,
                pageSize: options.pageSize || 20,
            };

            // Map sort option to backend enum values
            if (options.sort) {
                params.sort = options.sort;
            }

            if (options.category) {
                params.category = options.category;
            }

            if (options.search) {
                params.search = options.search;
            }

            if (options.status) {
                params.status = options.status;
            }

            if (options.authorId) {
                params.authorId = options.authorId;
            }

            if (options.tags) {
                params.tags = options.tags;
            }

            if (options.featured !== undefined) {
                params.featured = options.featured;
            }

            const response = await this.apiService.requestWithParams(
                '/wiki/articles',
                'GET',
                params
            );

            if (response && response.data !== undefined) {
                return new Response({
                    success: true,
                    data: response.data || [],
                    meta: response.meta,
                    message: 'Wiki articles fetched successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to fetch wiki articles',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch wiki articles',
                error,
            });
        }
    }

    // ================= Reactions (emoji-based) =================
    async addWikiArticleReaction(articleId, emoji) {
        return this.apiService.requestWithRawJson(`/wiki/articles/${articleId}/reactions`, 'POST', { emoji })
            .then((res) => res.data)
            .catch((err) => { throw err; });
    }

    async removeWikiArticleReaction(articleId, emoji) {
        return this.apiService.requestWithParams(`/wiki/articles/${articleId}/reactions`, 'DELETE', { emoji })
            .then((res) => res.data)
            .catch((err) => { throw err; });
    }

    async getWikiArticleReactions(articleId) {
        return this.apiService.requestWithParams(`/wiki/articles/${articleId}/reactions`, 'GET')
            .then((res) => res.data)
            .catch((err) => { throw err; });
    }

    /**
     * Get current user's wiki articles (including drafts)
     * @param {Object} options
     * @param {string} [options.status] - Optional status filter (draft|pending|published|rejected)
     * @returns {Promise<Response>}
     */
    async getMyWikiArticles(options = {}) {
        try {
            const params = {};
            if (options.status) {
                params.status = options.status;
            }

            const response = await this.apiService.requestWithParams('/wiki/articles/me', 'GET', params);

            if (response && response.data !== undefined) {
                return new Response({
                    success: true,
                    data: response.data || [],
                    meta: response.meta,
                    message: response.message || 'My wiki articles fetched successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to fetch my wiki articles',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch my wiki articles',
                error,
            });
        }
    }

    /**
     * Get a single wiki article by ID
     * @param {string} articleId - Article ID
     * @returns {Promise<Response>} - Response containing article data
     */
    async getWikiArticle(articleId) {
        try {
            const response = await this.apiService.get(`/wiki/articles/${articleId}`);

            if (response.data) {
                return new Response({
                    success: true,
                    data: response.data,
                    message: 'Wiki article fetched successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Article not found',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch wiki article',
                error,
            });
        }
    }

    /**
     * Create a new wiki article
     * @param {Object} articleData - Article data
     * @param {string} articleData.title - Article title
     * @param {string} articleData.content - Article content (HTML/Markdown)
     * @param {string} articleData.category - Article category
     * @param {string} [articleData.excerpt] - Article excerpt
     * @param {Array} [articleData.tags] - Article tags
     * @param {Array} [articleData.images] - Article images
     * @returns {Promise<Response>} - Response containing created article
     */
    async createWikiArticle(articleData) {
        try {
            const response = await this.apiService.post('/wiki/articles', articleData);

            if (response.data) {
                return new Response({
                    success: true,
                    data: response.data.data || response.data,
                    message: response.data.message || 'Wiki article created successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to create wiki article',
            });
        } catch (error) {
            const errorMessage = error.response?.data?.message 
                ? (Array.isArray(error.response.data.message) 
                    ? error.response.data.message.join(', ') 
                    : error.response.data.message)
                : (error.message || 'Failed to create wiki article');
            
            return new Response({
                success: false,
                message: errorMessage,
                error,
            });
        }
    }

    /**
     * Update a wiki article
     * @param {string} articleId - Article ID
     * @param {Object} updateData - Data to update
     * @returns {Promise<Response>} - Response containing updated article
     */
    async updateWikiArticle(articleId, updateData) {
        try {
            const response = await this.apiService.put(`/wiki/articles/${articleId}`, updateData);

            if (response.data) {
                return new Response({
                    success: true,
                    data: response.data.data || response.data,
                    message: response.data.message || 'Wiki article updated successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to update wiki article',
            });
        } catch (error) {
            const errorMessage = error.response?.data?.message 
                ? (Array.isArray(error.response.data.message) 
                    ? error.response.data.message.join(', ') 
                    : error.response.data.message)
                : (error.message || 'Failed to update wiki article');
            
            return new Response({
                success: false,
                message: errorMessage,
                error,
            });
        }
    }

    /**
     * Delete a wiki article
     * @param {string} articleId - Article ID
     * @returns {Promise<Response>} - Response indicating success
     */
    async deleteWikiArticle(articleId) {
        try {
            await this.apiService.delete(`/wiki/articles/${articleId}`);

            return new Response({
                success: true,
                message: 'Wiki article deleted successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to delete wiki article',
                error,
            });
        }
    }

    /**
     * Get wiki categories
     * @returns {Promise<Response>} - Response containing categories
     */
    async getWikiCategories() {
        try {
            const response = await this.apiService.get('/wiki/categories');

            if (response.data) {
                return new Response({
                    success: true,
                    data: response.data.data || response.data,
                    message: 'Wiki categories fetched successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to fetch wiki categories',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch wiki categories',
                error,
            });
        }
    }

    /**
     * Like/unlike a wiki article
     * @param {string} articleId - Article ID
     * @returns {Promise<Response>} - Response indicating success
     */
    async toggleWikiArticleLike(articleId) {
        try {
            const response = await this.apiService.post(`/wiki/articles/${articleId}/like`);
            const payload = response.data?.data ?? response.data;

            return new Response({
                success: true,
                data: payload,
                message: payload?.liked ? 'Article liked' : 'Article unliked',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to toggle article like',
                error,
            });
        }
    }

    /**
     * Get comments for a wiki article
     * @param {string} articleId - Article ID
     * @param {Object} options - Query options
     * @param {number} [options.page=1] - Page number
     * @param {number} [options.pageSize=20] - Items per page
     * @returns {Promise<Response>} - Response containing comments
     */
    async getWikiArticleComments(articleId, options = {}) {
        try {
            const params = {
                page: options.page || 1,
                pageSize: options.pageSize || 20,
            };

            const response = await this.apiService.requestWithParams(
                `/wiki/articles/${articleId}/comments`,
                'GET',
                params
            );

            if (response.data) {
                return new Response({
                    success: true,
                    data: response.data || [],
                    message: 'Comments fetched successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to fetch comments',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch comments',
                error,
            });
        }
    }

    /**
     * Add a comment to a wiki article
     * @param {string} articleId - Article ID
     * @param {Object} commentData - Comment data
     * @param {string} commentData.content - Comment content
     * @param {string} [commentData.parentId] - Parent comment ID for replies
     * @returns {Promise<Response>} - Response containing created comment
     */
    async addWikiArticleComment(articleId, commentData) {
        try {
            const response = await this.apiService.post(
                `/wiki/articles/${articleId}/comments`,
                commentData
            );

            if (response.data) {
                return new Response({
                    success: true,
                    data: response.data?.data ?? response.data,
                    message: 'Comment added successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to add comment',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to add comment',
                error,
            });
        }
    }

    /**
     * Like/unlike a wiki comment
     * @param {string} articleId - Article ID
     * @param {string} commentId - Comment ID
     * @returns {Promise<Response>} - Response indicating success
     */
    async toggleWikiCommentLike(articleId, commentId) {
        try {
            const response = await this.apiService.post(`/wiki/comments/${commentId}/like`);
            const payload = response.data?.data ?? response.data;

            return new Response({
                success: true,
                data: payload,
                message: payload?.liked ? 'Comment liked' : 'Comment unliked',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to toggle comment like',
                error,
            });
        }
    }

    /**
     * Update a comment
     * @param {string} articleId - Article ID
     * @param {string} commentId - Comment ID
     * @param {Object} updateData - Data to update
     * @returns {Promise<Response>} - Response containing updated comment
     */
    async updateWikiComment(articleId, commentId, updateData) {
        try {
            const response = await this.apiService.put(
                `/wiki/comments/${commentId}`,
                updateData
            );

            if (response.data) {
                return new Response({
                    success: true,
                    data: response.data?.data ?? response.data,
                    message: 'Comment updated successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to update comment',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to update comment',
                error,
            });
        }
    }

    /**
     * Delete a comment
     * @param {string} articleId - Article ID
     * @param {string} commentId - Comment ID
     * @returns {Promise<Response>} - Response indicating success
     */
    async deleteWikiComment(articleId, commentId) {
        try {
            await this.apiService.delete(`/wiki/comments/${commentId}`);

            return new Response({
                success: true,
                message: 'Comment deleted successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to delete comment',
                error,
            });
        }
    }

    /**
     * Submit article for approval
     * @param {string} articleId - Article ID
     * @returns {Promise<Response>} - Response indicating success
     */
    async submitWikiArticleForApproval(articleId) {
        try {
            const response = await this.apiService.post(`/wiki/articles/${articleId}/submit`);

            return new Response({
                success: true,
                data: response.data,
                message: 'Article submitted for approval',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to submit article for approval',
                error,
            });
        }
    }

    /**
     * Get pending articles (admin/moderator only)
     * @returns {Promise<Response>} - Response containing pending articles
     */
    async getPendingWikiArticles() {
        try {
            const response = await this.apiService.get('/wiki/articles/pending');

            if (response.data) {
                return new Response({
                    success: true,
                    data: response.data,
                    message: 'Pending articles fetched successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to fetch pending articles',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch pending articles',
                error,
            });
        }
    }

    /**
     * Approve a wiki article (admin/moderator only)
     * @param {string} articleId - Article ID
     * @returns {Promise<Response>} - Response indicating success
     */
    async approveWikiArticle(articleId) {
        try {
            const response = await this.apiService.post(`/wiki/articles/${articleId}/approve`);

            return new Response({
                success: true,
                data: response.data,
                message: 'Article approved successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to approve article',
                error,
            });
        }
    }

    /**
     * Reject a wiki article (admin/moderator only)
     * @param {string} articleId - Article ID
     * @param {string} rejectionReason - Reason for rejection
     * @returns {Promise<Response>} - Response indicating success
     */
    async rejectWikiArticle(articleId, rejectionReason) {
        try {
            const response = await this.apiService.post(`/wiki/articles/${articleId}/reject`, {
                rejectionReason
            });

            return new Response({
                success: true,
                data: response.data,
                message: 'Article rejected successfully',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to reject article',
                error,
            });
        }
    }

    /**
     * Upload images for wiki article
     * @param {Array} images - Array of image objects
     * @returns {Promise<Response>} - Response containing uploaded image URLs
     */
    async uploadWikiImages(images) {
        try {
            const formData = new FormData();
            
            images.forEach((image, index) => {
                if (typeof image === 'string') {
                    // If it's a string URI
                    const uri = image;
                    const name = uri.split('/').pop();
                    const match = /\.(\w+)$/.exec(name);
                    const type = match ? `image/${match[1]}` : 'image/jpeg';
                    
                    formData.append('images', {
                        uri,
                        name,
                        type
                    });
                } else {
                    // Assume it's already a proper object
                    formData.append('images', image);
                }
            });

            const response = await this.apiService.post('/upload/image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data) {
                return new Response({
                    success: true,
                    data: response.data,
                    message: 'Images uploaded successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to upload images',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to upload images',
                error,
            });
        }
    }

    /**
     * Search wiki articles
     * @param {string} query - Search query
     * @param {Object} options - Search options
     * @param {number} [options.page=1] - Page number
     * @param {number} [options.pageSize=20] - Items per page
     * @param {string} [options.category] - Filter by category
     * @returns {Promise<Response>} - Response containing search results
     */
    async searchWikiArticles(query, options = {}) {
        try {
            const params = {
                search: query,
                page: options.page || 1,
                pageSize: options.pageSize || 20,
            };

            if (options.category) {
                params.category = options.category;
            }

            const response = await this.apiService.requestWithParams(
                '/wiki/articles',
                'GET',
                params
            );

            if (response.data) {
                return new Response({
                    success: true,
                    data: response.data.data || [],
                    meta: response.data.meta,
                    message: 'Search results fetched successfully',
                });
            }

            return new Response({
                success: false,
                message: 'No search results found',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to search articles',
                error,
            });
        }
    }
}

export default WikiController;
